import React from "react";
import { Box, Typography, Paper, Switch, Collapse, Divider } from "@mui/material";
import CustomSelect from "../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../FormFields/CustomInput/CustomInput";

const ConditionMapping = ({ value = {}, onChange }) => {
  const sections = [
    { key: "diabetes", title: "Diabetes", q: "Is the Service User Diabetic?" },
    { key: "choking", title: "Choking/Dysphagia", q: "Is Service User at risk of choking?" },
    { key: "capabilities", title: "Capabilities", q: "Physical/Mental assessment" },
    { key: "communication", title: "Communication", q: "Speech and hearing requirements" },
    { key: "behaviour", title: "Behaviour", q: "Challenging behavior management" },
    { key: "falls", title: "Falls", q: "Risk of falling or mobility" },
    { key: "dols", title: "DoLS", q: "Deprivation of Liberty" },
    { key: "copd", title: "COPD/Respiratory", q: "Respiratory health" }
  ];

  const handleToggle = (key) => {
    const current = value[key] || { enabled: false };
    onChange({ ...value, [key]: { ...current, enabled: !current.enabled } });
  };

  const updateField = (key, field, val) => {
    onChange({ ...value, [key]: { ...(value[key] || {}), [field]: val } });
  };

  return (
    <Box>
      {sections.map(sec => (
        <Paper 
          key={sec.key} 
          elevation={0}
          sx={{ 
            mb: 2, 
            borderRadius: "12px", 
            border: "1px solid #e2e8f0", 
            overflow: "hidden",
            transition: "all 0.2s ease",
            "&:hover": { 
              borderColor: "#6366f1", 
              boxShadow: "0 4px 12px rgba(99,102,241,0.05)" 
            }
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: "center", 
            bgcolor: value[sec.key]?.enabled ? "#f5f3ff" : "white" 
          }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                {sec.title}
              </Typography>
              <Typography variant="caption" color="#64748b">
                {sec.q}
              </Typography>
            </Box>
            <Switch 
              checked={value[sec.key]?.enabled || false} 
              onChange={() => handleToggle(sec.key)}
              sx={{ 
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366f1" }, 
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#6366f1" } 
              }}
            />
          </Box>
          <Collapse in={value[sec.key]?.enabled}>
            <Divider />
            <Box sx={{ p: 3, bgcolor: "white" }}>
              {sec.key === 'diabetes' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        label="Type"
                        value={value.diabetes?.type || ""}
                        onChange={(e) => updateField('diabetes', 'type', e.target.value)}
                        options={[
                          { label: 'Type 1', value: 'Type 1' },
                          { label: 'Type 2', value: 'Type 2' }
                        ]}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        label="Controlled By"
                        value={value.diabetes?.controlledBy || ""}
                        onChange={(e) => updateField('diabetes', 'controlledBy', e.target.value)}
                        options={[
                          { label: 'Diet', value: 'Diet' },
                          { label: 'Medication', value: 'Medication' },
                          { label: 'Insulin', value: 'Insulin' }
                        ]}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <CustomInput
                      label="Management Notes"
                      value={value.diabetes?.notes || ""}
                      onChange={(e) => updateField('diabetes', 'notes', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Box>
                </Box>
              )}
              {sec.key === 'choking' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        label="Food Consistency"
                        value={value.choking?.food || ""}
                        onChange={(e) => updateField('choking', 'food', e.target.value)}
                        options={[
                          { label: 'Regular', value: 'Regular' },
                          { label: 'Soft', value: 'Soft' },
                          { label: 'Pureed', value: 'Pureed' }
                        ]}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <CustomSelect
                        label="Drink Consistency"
                        value={value.choking?.drink || ""}
                        onChange={(e) => updateField('choking', 'drink', e.target.value)}
                        options={[
                          { label: 'Thin', value: 'Thin' },
                          { label: 'Mildly Thick', value: 'Mildly Thick' },
                          { label: 'Thick', value: 'Thick' }
                        ]}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <CustomInput
                      label="Clinical Notes"
                      value={value.choking?.notes || ""}
                      onChange={(e) => updateField('choking', 'notes', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Box>
                </Box>
              )}
              {!['diabetes', 'choking'].includes(sec.key) && (
                <CustomInput
                  label={`${sec.title} Details`}
                  placeholder={`Enter details for ${sec.title}...`}
                  value={value[sec.key]?.notes || ""}
                  onChange={(e) => updateField(sec.key, 'notes', e.target.value)}
                  multiline
                  rows={3}
                />
              )}
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );
};

export default ConditionMapping;